'use client';

import { useEffect } from 'react';
import { track } from '@vercel/analytics';

// Mirrors GTM GA4 events driven by Link Click triggers:
//   - `appointment_and_phone_combined` — Click URL starts with `tel:` OR contains `#schedule`
//   - `appointment_request_cta`         — Click URL contains `#schedule`
//   - `phone_call_cta`                  — Click URL starts with `tel:`
//
// Uses document-level delegation so no existing component needs to change.
// Runs in capture phase and does NOT check `defaultPrevented` because
// SchedulerProTrigger deliberately preventDefaults `#schedule` clicks to open
// the scheduler modal — we still want to count those as appointment requests.
const COMBINED_EVENT = 'appointment_and_phone_combined';
const REQUEST_CTA_EVENT = 'appointment_request_cta';
const PHONE_CTA_EVENT = 'phone_call_cta';
const SCHEDULE_MATCH = '#schedule';
const DEDUPE_WINDOW_MS = 500;

function getAnchorFromEvent(event: MouseEvent): HTMLAnchorElement | null {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  return target.closest('a');
}

export default function AppointmentPhoneTracker() {
  useEffect(() => {
    let lastScheduleTrackAt = 0;
    let lastPhoneTrackAt = 0;

    const trackScheduleEvents = (source: 'link_click' | 'programmatic' | 'initial_url') => {
      if (Date.now() - lastScheduleTrackAt < DEDUPE_WINDOW_MS) return;
      lastScheduleTrackAt = Date.now();
      const payload = {
        trigger: 'schedule_online',
        source,
        page: window.location.pathname,
      } as const;
      track(COMBINED_EVENT, payload);
      track(REQUEST_CTA_EVENT, payload);
    };

    const trackPhoneEvents = (source: 'link_click' | 'aux_click') => {
      if (Date.now() - lastPhoneTrackAt < DEDUPE_WINDOW_MS) return;
      lastPhoneTrackAt = Date.now();
      const payload = { trigger: 'phone_number', source, page: window.location.pathname } as const;
      track(COMBINED_EVENT, payload);
      track(PHONE_CTA_EVENT, payload);
    };

    const handleClick = (event: MouseEvent) => {
      const anchor = getAnchorFromEvent(event);
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const source = event.type === 'auxclick' ? 'aux_click' : 'link_click';

      if (href.startsWith('tel:')) {
        trackPhoneEvents(source);
        return;
      }

      if (href.includes(SCHEDULE_MATCH)) {
        trackScheduleEvents('link_click');
      }
    };

    const handleHashChange = () => {
      if (!window.location.hash.includes(SCHEDULE_MATCH)) return;
      trackScheduleEvents('programmatic');
    };

    // Fire on initial page load when URL already contains `#schedule`.
    // SchedulerProTrigger opens the scheduler in this case, but neither
    // a click nor a hashchange event fires — so it would otherwise be missed.
    if (typeof window !== 'undefined' && window.location.hash.includes(SCHEDULE_MATCH)) {
      trackScheduleEvents('initial_url');
    }

    document.addEventListener('click', handleClick, { capture: true });
    document.addEventListener('auxclick', handleClick, { capture: true });
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      document.removeEventListener('auxclick', handleClick, { capture: true });
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return null;
}
